import { Fragment, useState } from 'react';

import {
  Confirm,
  Identifier,
  useDataProvider,
  useListContext,
  useNotify,
} from 'react-admin';
import Button from '@material-ui/core/Button';
import some from 'lodash/some';
import { BumpResourceSheetPaidBatchInterface } from '@vatos-pas/common';

import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_APPROVE_BUMPOUT,
  CAN_EDIT_BUMPOUT_TIMESHEET_PAID,
  CAN_MANAGE_HO_SERVICE_REPAIR,
} from 'providers/permissions';
import { Show } from 'components/Show';
import {
  areAllSelectedRowsApprovable,
  dateRangeFields,
  MarkAsPaidButton,
} from 'components/SelectionToolkit';
import { BumpoutTimesheetFields } from '../views/bumpout-timesheet-list';
import {
  BUMPOUT_MARK_AS_PAID_RESOURCE,
  BumpoutSelectionRowsById,
  buildMarkAsPaidBody,
  isRowApprovableBy,
  markAsPaidWarnings,
} from '../selection';
import { DateTime } from 'luxon';

export const BUMPOUT_MARK_AS_PAID_TOOLTIP = [
  'Required Filters:',
  'On and Before',
  'On and After',
];
const REPAIR_DATE_RANGE = dateRangeFields('repairDate');

type BulkActionsButtonsProps = {
  fields: BumpoutTimesheetFields | null;
  /** Rows seen so far, used to judge approvability and split by row type. */
  selectionRows: BumpoutSelectionRowsById;
  selectedIds?: Identifier[];
};

const BulkActionButtons = ({
  fields,
  selectionRows,
  selectedIds = [],
}: BulkActionsButtonsProps) => {
  const { data, onUnselectItems, refetch } = useListContext();
  const { hasPermission } = PermissionsProvider.useContainer();
  const notify = useNotify();
  const dataProvider = useDataProvider();

  const [open, setOpen] = useState(false);

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => setOpen(false);

  const canApprove = hasPermission(CAN_APPROVE_BUMPOUT);
  const canManageHOService = hasPermission(CAN_MANAGE_HO_SERVICE_REPAIR);

  const hasWorkedHoursZeroed = some(fields, { workedHours: '0' });
  const hasDirtyWorkedHours = selectedIds.filter(id => {
    const timeIn =
      fields?.[id]?.timeIn instanceof DateTime &&
      (fields[id].timeIn as DateTime).toFormat('HH:mm:00');

    const timeOut =
      fields?.[id]?.timeOut instanceof DateTime &&
      (fields[id].timeOut as DateTime).toFormat('HH:mm:00');

    const diffTimeIn = timeIn && timeIn !== data?.[id].timeIn;
    const diffTimeOut = timeOut && timeOut !== data?.[id].timeOut;

    return diffTimeIn || diffTimeOut;
  });
  const isSelectionApprovable = areAllSelectedRowsApprovable(
    selectedIds,
    selectionRows,
    row => isRowApprovableBy(row, { canApprove, canManageHOService }),
  );

  const handleConfirm = async () => {
    try {
      await dataProvider.create('repair-payment/batch-process', {
        data: {
          update: selectedIds.map(id => ({
            id,
            isApprovedHours: true,
            ...(fields?.[id]?.workedHours && {
              approvedHours: Number(fields[id].workedHours),
            }),
          })),
        },
      });
      notify('Approved successfully.');
      onUnselectItems();
      refetch();
    } catch (error) {
      notify(error.message, 'warning');
    } finally {
      setOpen(false);
    }
  };

  return (
    <Fragment>
      <Show condition={canApprove}>
        <Button
          disabled={
            !!hasDirtyWorkedHours.length ||
            hasWorkedHoursZeroed ||
            !isSelectionApprovable
          }
          onClick={handleClick}
        >
          Approve
        </Button>
      </Show>
      <MarkAsPaidButton<BumpResourceSheetPaidBatchInterface>
        permission={CAN_EDIT_BUMPOUT_TIMESHEET_PAID}
        dateRange={REPAIR_DATE_RANGE}
        tooltipLines={BUMPOUT_MARK_AS_PAID_TOOLTIP}
        resource={BUMPOUT_MARK_AS_PAID_RESOURCE}
        buildBody={ids => buildMarkAsPaidBody(ids, selectionRows)}
        warningsFor={markAsPaidWarnings}
      />
      <Confirm
        isOpen={open}
        title="Approve logs"
        content="Are you sure you want to approve these logs?"
        onConfirm={handleConfirm}
        onClose={handleDialogClose}
      />
    </Fragment>
  );
};

export default BulkActionButtons;
